// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — API Client
// ═══════════════════════════════════════════════════════════

const BASE_URL = '/api';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('arams_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options?.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    localStorage.removeItem('arams_token');
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || 'Request failed');
  }

  return res.json();
}

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, name: string, password: string) =>
    request<{ token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password }),
    }),

  getMe: () => request<User>('/auth/me'),

  // Auth — Password Reset
  forgotPassword: (email: string) =>
    request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (token: string, password: string) =>
    request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  // Projects
  listProjects: () => request<Project[]>('/projects'),
  getProject: (id: number) => request<ProjectDetail>(`/projects/${id}`),
  createProject: (data: Partial<Project>) =>
    request<Project>('/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateProject: (id: number, data: Partial<Project>) =>
    request<Project>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProject: (id: number) =>
    request<{ message: string }>(`/projects/${id}`, { method: 'DELETE' }),
  createRole: (projectId: number, data: Partial<TestRole>) =>
    request<TestRole>(`/projects/${projectId}/roles`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createSuite: (projectId: number, data: Partial<TestSuite>) =>
    request<TestSuite>(`/projects/${projectId}/suites`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Runs
  createRun: (data: Partial<TestRun>) =>
    request<TestRun>('/runs', { method: 'POST', body: JSON.stringify(data) }),
  getRun: (runId: number) => request<RunDetail>(`/runs/${runId}`),
  getRunResults: (runId: number) => request<TestResult[]>(`/runs/${runId}/results`),
  getRunSteps: (runId: number) => request<TestResultWithSteps[]>(`/runs/${runId}/steps`),
  getRunScreenshots: (runId: number) => request<Screenshot[]>(`/runs/${runId}/screenshots`),
  cancelRun: (runId: number) =>
    request<{ message: string }>(`/runs/${runId}`, { method: 'DELETE' }),

  // Results
  getResult: (resultId: number) => request<TestResultWithSteps>(`/results/${resultId}`),

  // Screenshots
  approveScreenshot: (id: number) =>
    request<{ message: string }>(`/screenshots/${id}/approve`, { method: 'PUT' }),

  // AI
  generateTest: (data: {
    projectId: number;
    description: string;
    roleName?: string;
    testType?: string;
  }) => request<AiGeneration>('/ai/generate-test', { method: 'POST', body: JSON.stringify(data) }),
  generateSeed: (data: { projectId: number; description: string }) =>
    request<AiGeneration>('/ai/generate-seed', { method: 'POST', body: JSON.stringify(data) }),
  fixTest: (data: {
    projectId: number;
    testName: string;
    errorMessage: string;
    failedSelector?: string;
    pageHtml?: string;
  }) =>
    request<{ fix: string; model: string }>('/ai/fix-test', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  aiHistory: (projectId: number) => request<AiGeneration[]>(`/ai/history/${projectId}`),

  // AI Models & Providers
  getAiProviders: () => request<AiProvider[]>('/ai/providers'),
  getAiModels: (provider?: string) =>
    request<AiModel[]>(`/ai/models${provider ? `?provider=${provider}` : ''}`),
  getRecommendedModel: (provider: string) =>
    request<{ name: string; displayName: string; provider: string }>(
      `/ai/models/recommended/${provider}`,
    ),
  verifyAiKey: (provider: string, apiKey: string) =>
    request<{ valid: boolean; message: string }>('/ai/verify-key', {
      method: 'POST',
      body: JSON.stringify({ provider, apiKey }),
    }),
  saveProjectAiConfig: (data: {
    projectId: number;
    provider: string;
    model: string;
    apiKey?: string;
  }) =>
    request<{ saved: boolean; encrypted: boolean }>('/ai/save-project-key', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  refreshAiModels: () =>
    request<{ refreshed: number; message: string }>('/ai/models/refresh', { method: 'POST' }),

  // Analytics
  getTrends: (projectId: number, days?: number) =>
    request<{ trends: TrendPoint[]; summary: TrendSummary }>(
      `/analytics/trends/${projectId}?days=${days || 30}`,
    ),
  getFlakyTests: (projectId: number) =>
    request<{ flakyTests: FlakyTest[] }>(`/analytics/flaky/${projectId}`),
  getCoverage: (projectId: number) =>
    request<{ coverage: CoverageData }>(`/analytics/coverage/${projectId}`),

  // GitHub
  getGithubStatus: (projectId: number) => request<GithubStatus>(`/github/status/${projectId}`),
  getGithubUserStatus: () =>
    request<{
      connected: boolean;
      webhookActive: boolean;
      oauthConfigured?: boolean;
      githubUser?: { login: string; name: string; avatarUrl: string };
    }>('/github/status'),
  connectGithub: () => request<{ authUrl: string }>('/github/connect', { method: 'POST' }),
  saveGithubToken: (token: string) =>
    request<{ connected: boolean; githubUser: { login: string; name: string; avatarUrl: string } }>(
      '/github/token',
      {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
    ),
  disconnectGithub: (projectId: number) =>
    request<{ message: string }>('/github/disconnect', {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    }),
  listGithubRepos: () => request<GithubRepo[]>('/github/repos'),
  listGithubBranches: (owner: string, repo: string) =>
    request<GithubBranch[]>(`/github/repos/${owner}/${repo}/branches`),
  analyzeRepo: (repoFullName: string, branch?: string) =>
    request<RepoAnalysis>('/github/analyze-repo', {
      method: 'POST',
      body: JSON.stringify({ repoFullName, branch }),
    }),
  setupGithubWebhook: (projectId: number, owner: string, repo: string) =>
    request<{ message: string }>('/github/webhook/setup', {
      method: 'POST',
      body: JSON.stringify({ projectId, owner, repo }),
    }),

  // Runs — Enhanced
  startRun: (data: StartRunConfig) =>
    request<TestRun>('/runs/start', { method: 'POST', body: JSON.stringify(data) }),
  getActiveRuns: () => request<ActiveRun[]>('/runs/active'),
  getRunProgress: (runId: number) => request<RunProgress>(`/runs/${runId}/progress`),

  // Email
  getEmailStatus: () => request<{ connected: boolean; transport: string }>('/email/status'),
  sendTestEmail: (to: string) =>
    request<{ message: string }>('/email/test', { method: 'POST', body: JSON.stringify({ to }) }),
  sendInviteEmail: (data: { to: string; name: string; inviterName: string }) =>
    request<{ message: string }>('/email/invite', { method: 'POST', body: JSON.stringify(data) }),
  sendRunReport: (runId: number, recipients: string[]) =>
    request<{ message: string }>('/email/run-report', {
      method: 'POST',
      body: JSON.stringify({ runId, recipients }),
    }),

  // Admin
  adminListUsers: () => request<AdminUser[]>('/admin/users'),
  adminGetUser: (id: number) => request<AdminUser>(`/admin/users/${id}`),
  adminUpdateUser: (id: number, data: Partial<AdminUser>) =>
    request<AdminUser>(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  adminDeleteUser: (id: number) =>
    request<{ message: string }>(`/admin/users/${id}`, { method: 'DELETE' }),
  adminGetStats: () => request<AdminStats>('/admin/stats'),
};

// ── Types ────────────────────────────────────────────────

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  avatarUrl?: string;
  createdAt?: string;
  lastLogin?: string;
  licenseTier?: string;
  licenseExpiresAt?: string | null;
  isActive?: boolean;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
  avatarUrl?: string;
  isActive: boolean;
  emailVerified?: boolean;
  licenseTier: string;
  licenseExpiresAt?: string | null;
  createdAt?: string;
  lastLogin?: string;
  projectCount?: number;
}

export interface AdminStats {
  users: { total: number; active: number };
  projects: { total: number };
  licenses: Record<string, number>;
}

export interface Project {
  projectId: number;
  projectName: string;
  projectSlug: string;
  projectDescription?: string;
  projectGithubRepo?: string;
  projectGithubBranch?: string;
  projectTargetUrl?: string;
  projectStatus: string;
  projectCreatedAt: string;
  lastRun?: TestRun | null;
  roleCount?: number;
  suiteCount?: number;
}

export interface ProjectDetail extends Project {
  roles: TestRole[];
  suites: TestSuite[];
  recentRuns: TestRun[];
}

export interface TestRole {
  roleId: number;
  roleName: string;
  roleEmail: string;
  roleBusinessType?: string;
  roleSubType?: string;
  roleDescription?: string;
  roleLandingPage?: string;
}

export interface TestSuite {
  suiteId: number;
  suiteName: string;
  suiteType: string;
  suiteFilePath: string;
  suitePriority: string;
  suiteTags: string[];
  suiteEnabled: boolean;
}

export interface TestRun {
  runId: number;
  projectId: number;
  runTrigger: string;
  runStatus: string;
  runBranch?: string;
  runCommitSha?: string;
  runPrNumber?: number;
  runTotalTests?: number;
  runPassedTests?: number;
  runFailedTests?: number;
  runSkippedTests?: number;
  runDurationMs?: number;
  runStartedAt?: string;
  runCompletedAt?: string;
  runCreatedAt: string;
}

export interface RunDetail extends TestRun {
  results: TestResult[];
}

export interface TestResult {
  resultId: number;
  runId: number;
  resultTestName: string;
  resultStatus: string;
  resultDurationMs?: number;
  resultErrorMessage?: string;
  resultErrorStack?: string;
  resultRetryCount: number;
  resultBrowser: string;
}

export interface TestResultWithSteps extends TestResult {
  steps: TestStep[];
}

export interface TestStep {
  stepId: number;
  stepNumber: number;
  stepAction: string;
  stepTarget?: string;
  stepStatus: string;
  stepDurationMs?: number;
  stepScreenshotUrl?: string;
}

export interface Screenshot {
  screenshotId: number;
  screenshotType: string;
  screenshotName: string;
  screenshotPath: string;
  screenshotDiffPercentage?: number;
}

export interface AiGeneration {
  generationId: number;
  generationType: string;
  generationInput: string;
  generationOutput?: string;
  generationModel: string;
  generationTokensUsed?: number;
  generationStatus: string;
  generationCreatedAt: string;
  code?: string;
  model?: string;
  tokensUsed?: number;
}

export interface TrendPoint {
  runId: number;
  status: string;
  totalTests?: number;
  passedTests?: number;
  failedTests?: number;
  durationMs?: number;
  createdAt: string;
}

export interface TrendSummary {
  totalRuns: number;
  passedRuns: number;
  failedRuns: number;
  avgDuration: number;
}

export interface FlakyTest {
  testName: string;
  passCount: number;
  failCount: number;
  flakyRate: number;
}

export interface CoverageData {
  totalTests: number;
  passed: number;
  failed: number;
  byType: Record<string, number>;
}

export interface GithubStatus {
  connected: boolean;
  webhookActive: boolean;
  message: string;
  user?: { login: string; avatarUrl: string };
}

export interface GithubRepo {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  htmlUrl: string;
  language: string | null;
  updatedAt: string;
}

export interface GithubBranch {
  name: string;
  commit: { sha: string };
  protected: boolean;
}

export interface StartRunConfig {
  projectId: number;
  branch?: string;
  suiteIds?: number[];
  tags?: string[];
  browsers?: string[];
  headed?: boolean;
  retries?: number;
  workers?: number;
}

export interface ActiveRun {
  runId: number;
  projectId: number;
  status: string;
  progress: RunProgress;
}

export interface RunProgress {
  total: number;
  completed: number;
  passed: number;
  failed: number;
  skipped: number;
  percentage: number;
  currentTest?: string;
}

export interface AiProvider {
  provider: string;
  displayName: string;
  platformConfigured: boolean;
  description: string;
}

export interface AiModel {
  modelId: number;
  provider: string;
  name: string;
  displayName: string;
  description?: string;
  isRecommended: boolean;
  maxInputTokens?: number;
  maxOutputTokens?: number;
  costPerInputMtoken?: number;
  costPerOutputMtoken?: number;
  capabilities?: string[];
  lastVerifiedAt?: string;
}

// ── Repo Analysis Types ──────────────────────────────────

export interface RepoAnalysisAuth {
  hasAuth: boolean;
  loginPath: string;
  usernameSelector: string;
  passwordSelector: string;
  submitSelector: string;
  authMethod: string;
}

export interface RepoAnalysisCategory {
  id: string;
  name: string;
  description: string;
  priority: string;
  estimatedTests: number;
  examples: string[];
}

export interface RepoAnalysisRole {
  name: string;
  description: string;
  permissions: string[];
  landingPage: string;
}

export interface RepoAnalysisSuggestedTest {
  name: string;
  category: string;
  description: string;
  role: string | null;
  priority: string;
  type: string;
}

export interface RepoAnalysisResult {
  projectName: string;
  description: string;
  techStack: string[];
  targetUrl: string;
  defaultBranch: string;
  auth: RepoAnalysisAuth;
  testCategories: RepoAnalysisCategory[];
  userRoles: RepoAnalysisRole[];
  suggestedTests: RepoAnalysisSuggestedTest[];
  missingInfo: string[];
  securityChecks: string[];
}

export interface RepoAnalysis {
  analysis: RepoAnalysisResult;
  model: string;
  repoInfo: {
    description: string;
    language: string;
    homepage: string;
    defaultBranch: string;
    private: boolean;
  };
}
